import * as THREE from 'three';

export const SNAPSHOT_RENDER_WIDTH = 1920;
export const SNAPSHOT_RENDER_HEIGHT = 1080;

/** Master PDF label quad size in the 1080p capture, before view multipliers. */
const LABEL_WIDTH_PX = 78;
const LABEL_HEIGHT_PX = 32;
const KITCHEN_PLAN_MULTIPLIER = 0.8;
const ELEVATION_MULTIPLIER = 1.15;

type SpriteScaleSnapshot = {
    sprite: THREE.Sprite<THREE.Object3DEventMap>;
    scale: THREE.Vector3;
};

const isSprite = (
    object: THREE.Object3D
): object is THREE.Sprite<THREE.Object3DEventMap> =>
    object instanceof THREE.Sprite;

/**
 * World size of a measurement label in the PDF capture. Matches master: a
 * fixed on-page size (78×32px at 1080p), with plan slightly smaller than
 * elevations so every view stays readable and does not change with room size.
 * @param {number} frustumHeight - Orthographic camera frustum height
 * @param {boolean} isKitchenPlan - Plan view uses the 0.8 multiplier
 * @param {number} renderHeight - Capture buffer height in pixels
 * @return {{width: number, height: number}} Sprite scale in scene units
 */
export const getSnapshotLabelWorldSize = (
    frustumHeight: number,
    isKitchenPlan: boolean,
    renderHeight: number = SNAPSHOT_RENDER_HEIGHT
): {width: number; height: number} => {
    if (!(frustumHeight > 0) || !(renderHeight > 0)) {
        return {width: LABEL_WIDTH_PX, height: LABEL_HEIGHT_PX};
    }

    let unitsPerPixel = frustumHeight / renderHeight;
    unitsPerPixel *= isKitchenPlan
        ? KITCHEN_PLAN_MULTIPLIER
        : ELEVATION_MULTIPLIER;

    return {
        width: LABEL_WIDTH_PX * unitsPerPixel,
        height: LABEL_HEIGHT_PX * unitsPerPixel,
    };
};

/**
 * Scales sprites for a PDF capture using master's on-page size and returns
 * the originals so the live planner can be restored afterwards.
 * @param {THREE.Scene} scene - Room planner scene
 * @param {THREE.OrthographicCamera} camera - Capture camera after fitting
 * @param {boolean} isKitchenPlan - Whether this capture is the plan view
 * @return {SpriteScaleSnapshot[]} Original sprite scales
 */
export const applySnapshotLabelScales = (
    scene: THREE.Scene,
    camera: THREE.OrthographicCamera,
    isKitchenPlan: boolean
): SpriteScaleSnapshot[] => {
    const frustumHeight = Math.abs(camera.top - camera.bottom);
    const {width, height} = getSnapshotLabelWorldSize(
        frustumHeight,
        isKitchenPlan
    );
    const originals: SpriteScaleSnapshot[] = [];

    scene.traverse((object) => {
        if (!isSprite(object)) {
            return;
        }

        originals.push({sprite: object, scale: object.scale.clone()});
        object.scale.set(width, height, 1);
    });

    return originals;
};

/**
 * Restores sprites to the scales captured before PDF sizing.
 * @param {SpriteScaleSnapshot[]} originals - Saved sprite scales
 * @return {void}
 */
export const restoreSnapshotLabelScales = (
    originals: SpriteScaleSnapshot[]
): void => {
    originals.forEach(({sprite, scale}) => {
        sprite.scale.copy(scale);
    });
};
